/**
 * ActiveMedicationsGrid
 *
 * Compact 2-column grid summarizing the patient's currently-active medications.
 * Each cell: micro-label (med name, uppercase) + dose + sig + age-on-therapy.
 *
 * Pulls prescriptionService.getPrescriptionsByPatientId, filters to active, and
 * flattens the medicationRequests across active prescriptions. Does not replace
 * the full PatientPrescriptionsTab — it's an at-a-glance summary above it.
 */

import React, { useEffect, useState } from 'react';
import { Pill, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { prescriptionService, type Prescription } from '@/services/prescriptionService';

interface MedSummary {
  key: string;
  name: string;
  dose?: string;
  sig?: string;
  /** Days since the prescription was created. */
  ageDays?: number;
  controlled?: boolean;
}

interface ActiveMedicationsGridProps {
  patientId: string;
  onPrescribe?: () => void;
}

const formatAge = (days?: number): string | undefined => {
  if (days === undefined) return undefined;
  if (days < 1) return 'started today';
  if (days < 14) return `started ${days}d ago`;
  if (days < 60) return `started ${Math.round(days / 7)}wk ago`;
  if (days < 365) return `started ${Math.round(days / 30)}mo ago`;
  return `started ${Math.round(days / 365)}y ago`;
};

const ActiveMedicationsGrid: React.FC<ActiveMedicationsGridProps> = ({
  patientId,
  onPrescribe,
}) => {
  const [meds, setMeds] = useState<MedSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await prescriptionService.getPrescriptionsByPatientId(patientId, {
          page: 1,
          limit: 25,
          sortBy: 'created_at',
          sortOrder: 'DESC',
        });
        if (cancelled) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = res;
        // API envelope is { success, data: { prescriptions: [...], pagination } }.
        // Older callers received the array directly. Probe both shapes and
        // always coerce to an array so .filter() never throws.
        const candidates = [
          payload?.data?.data?.prescriptions,
          payload?.data?.prescriptions,
          payload?.data?.data,
          payload?.data,
          payload,
        ];
        const raw = candidates.find((c) => Array.isArray(c)) ?? [];
        const list: Prescription[] = raw as Prescription[];
        const active = list.filter(
          (p) => (p?.status ?? '').toLowerCase() === 'active',
        );

        const flat: MedSummary[] = [];
        active.forEach((rx) => {
          const ageDays = rx.created_at
            ? Math.max(
                0,
                Math.round((Date.now() - new Date(rx.created_at).getTime()) / 86_400_000),
              )
            : undefined;
          (rx.medicationRequests ?? []).forEach((mr, i) => {
            const name = (mr.medication_name || mr.medication || '').trim();
            if (!name) return;
            flat.push({
              key: `${rx.id}-${mr.id ?? i}`,
              name,
              dose: mr.dose,
              sig: mr.sig,
              ageDays,
              controlled:
                (mr.product?.controlled_indicator ?? '').toLowerCase() === 'y' ||
                Number(mr.product?.dea_schedule_level ?? 0) > 0,
            });
          });
        });

        // Dedupe by name+dose, keep newest (active rx already DESC by created_at)
        const seen = new Set<string>();
        const dedup: MedSummary[] = [];
        flat.forEach((m) => {
          const k = `${m.name.toLowerCase()}::${(m.dose ?? '').toLowerCase()}`;
          if (!seen.has(k)) {
            seen.add(k);
            dedup.push(m);
          }
        });

        setMeds(dedup.slice(0, 6));
      } catch (e) {
        if (!cancelled) {
          console.error('[ActiveMeds] failed to load:', e);
          setError('Unable to load active medications');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[68px] animate-pulse rounded-md bg-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
        {error}
      </div>
    );
  }

  if (meds.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Pill className="h-3.5 w-3.5 text-slate-400" />
            No active medications on file
          </div>
          {onPrescribe && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={onPrescribe}
            >
              <Plus className="h-3 w-3 mr-1" />
              Start prescription
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Active Medications · {meds.length}
        </span>
        {onPrescribe && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px] text-teal-700 hover:bg-teal-50"
            onClick={onPrescribe}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {meds.map((m) => (
          <div
            key={m.key}
            className="rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-2"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">
                {m.name}
              </span>
              {m.controlled && (
                <AlertTriangle
                  className="h-3 w-3 text-amber-500 flex-shrink-0"
                  aria-label="Controlled substance"
                />
              )}
            </div>
            <p className="mt-0.5 text-sm font-medium text-slate-900 leading-tight">
              {m.dose || '—'}
            </p>
            {m.sig && (
              <p className="mt-0.5 text-xs text-slate-500 leading-snug line-clamp-1">
                {m.sig}
              </p>
            )}
            {m.ageDays !== undefined && (
              <p className="mt-1 text-[11px] text-slate-400">{formatAge(m.ageDays)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActiveMedicationsGrid;
